/** King account numbers like 1211130-0 / 1212642-1 (6+ digits, hyphen, 1–3 digit suffix). */
export const KING_ACCOUNT_NUMBER_RE = /\b(\d{6,}-\d{1,3})\b/g

export function isKingAccountNumber(value: string): boolean {
  return /^\d{6,}-\d{1,3}$/.test(value.trim())
}

export function buildTenantAccountHref(tenantId: string, accountNumber: string): string {
  const params = new URLSearchParams({ serviceAccountNumber: accountNumber })
  return `/tenant/tenants/${encodeURIComponent(tenantId)}?${params.toString()}`
}

export function extractAccountNumberFromHref(href: string | undefined | null): string | null {
  if (!href) return null
  try {
    const url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    const san = url.searchParams.get('serviceAccountNumber')?.trim()
    if (san && isKingAccountNumber(san) && url.pathname.includes('/tenant/tenants/')) {
      return san
    }
  } catch {
    // ignore
  }
  return null
}

/** Prefer the first tenant cuid used in account deep links within markdown. */
export function extractTenantIdFromAccountLinks(markdown: string): string | undefined {
  const match = markdown.match(/\/tenant\/tenants\/([^/?#)\s]+)\?[^)\]]*serviceAccountNumber=/)
  const id = match?.[1]
  return id ? decodeURIComponent(id) : undefined
}

/**
 * Turn bare king account numbers into admin deep links for the focused tenant.
 * Skips fenced/inline code and existing markdown links.
 */
export function linkifyKingAccountNumbersInMarkdown(markdown: string, tenantId: string): string {
  if (!tenantId.trim()) return markdown

  const placeholders: string[] = []
  const protect = (chunk: string) => {
    const token = `\u0000ACCT${placeholders.length}\u0000`
    placeholders.push(chunk)
    return token
  }

  let text = markdown
    .replace(/```[\s\S]*?```/g, protect)
    .replace(/`[^`\n]+`/g, protect)
    .replace(/\[[^\]]*]\([^)]+\)/g, protect)

  text = text.replace(KING_ACCOUNT_NUMBER_RE, (accountNumber) => {
    return `[${accountNumber}](${buildTenantAccountHref(tenantId, accountNumber)})`
  })

  return text.replace(/\u0000ACCT(\d+)\u0000/g, (_, idx) => placeholders[Number(idx)] ?? '')
}
